// Recognized reporting-period id shapes. The rolling presets (week/biweek) are
// anchored on an end date; the calendar presets (cweek/cmonth) and arbitrary
// ranges are parsed by the backend. Anything else falls back to the biweekly
// preset so stale/legacy links stay usable.
const ROLLING_PATTERN = /^(?:week|biweek)(?:_|$)/;
const CWEEK_PATTERN = /^cweek_\d{4}-\d{2}-\d{2}$/;
const CMONTH_PATTERN = /^cmonth_\d{4}-\d{2}$/;
const RANGE_PATTERN = /^range_\d{4}-\d{2}-\d{2}_\d{4}-\d{2}-\d{2}$/;

export function normalizeReportingPeriodId(value) {
  const candidate = String(value || "");
  if (
    ROLLING_PATTERN.test(candidate)
    || CWEEK_PATTERN.test(candidate)
    || CMONTH_PATTERN.test(candidate)
    || RANGE_PATTERN.test(candidate)
  ) {
    return candidate;
  }
  return "biweek";
}

export function reportingPeriodKind(periodId) {
  const candidate = String(periodId || "");
  if (CWEEK_PATTERN.test(candidate)) return "cweek";
  if (CMONTH_PATTERN.test(candidate)) return "cmonth";
  if (RANGE_PATTERN.test(candidate)) return "range";
  return candidate.startsWith("week") ? "week" : "biweek";
}

// The rolling presets (7/14 days) can be re-anchored on a period's end date;
// the calendar and range presets carry their own explicit boundaries, so they
// pass straight through the kind selector without re-anchoring.
export function reportingPeriodIdForKind(kind, period) {
  if (kind === "cweek" || kind === "cmonth" || kind === "range") return kind;
  if (!period?.end_date || String(period.label || "").startsWith("Last ")) return kind;
  return `${kind}_${period.end_date}`;
}

// Build a calendar-week id (Monday-anchored) from an arbitrary date.
export function cweekPeriodId(date) {
  const parsed = date instanceof Date ? date : parseCalendarDate(date);
  if (!parsed) return null;
  const dayOfWeek = (parsed.getUTCDay() + 6) % 7; // 0 = Monday
  const monday = new Date(parsed.getTime() - dayOfWeek * 86400000);
  return `cweek_${monday.toISOString().slice(0, 10)}`;
}

// Build a calendar-month id (YYYY-MM) from an arbitrary date.
export function cmonthPeriodId(date) {
  const parsed = date instanceof Date ? date : parseCalendarDate(date);
  if (!parsed) return null;
  return `cmonth_${parsed.toISOString().slice(0, 7)}`;
}

// Build a single-day range id (start == end) from an ISO date string.
export function singleDayPeriodId(date) {
  const parsed = date instanceof Date ? date : parseCalendarDate(date);
  if (!parsed) return null;
  const iso = parsed.toISOString().slice(0, 10);
  return `range_${iso}_${iso}`;
}

// Build an arbitrary range id from two ISO date strings (ordered).
export function rangePeriodId(startDate, endDate) {
  const start = startDate instanceof Date ? startDate : parseCalendarDate(startDate);
  const end = endDate instanceof Date ? endDate : parseCalendarDate(endDate);
  if (!start || !end) return null;
  const [from, to] = start.getTime() <= end.getTime() ? [start, end] : [end, start];
  return `range_${from.toISOString().slice(0, 10)}_${to.toISOString().slice(0, 10)}`;
}

export function adjacentReportingPeriodId(period, direction) {
  if (![-1, 1].includes(direction)) return null;
  const kind = reportingPeriodKind(period?.id);
  const end = parseCalendarDate(period?.end_date);
  const start = parseCalendarDate(period?.start_date);
  if (!end || !start) return null;
  // Calendar presets step by whole calendar units so the boundaries stay aligned.
  if (kind === "cweek") {
    const nextMonday = new Date(start.getTime() + direction * 7 * 86400000);
    return `cweek_${nextMonday.toISOString().slice(0, 10)}`;
  }
  if (kind === "cmonth") {
    const anchor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + direction, 1));
    return `cmonth_${anchor.toISOString().slice(0, 7)}`;
  }
  // Rolling and arbitrary-range presets step by their own length.
  const dayCount = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
  if (kind === "range") {
    const nextStart = new Date(start.getTime() + direction * dayCount * 86400000);
    const nextEnd = new Date(nextStart.getTime() + (dayCount - 1) * 86400000);
    return rangePeriodId(nextStart, nextEnd);
  }
  const adjacentEnd = new Date(end.getTime() + direction * dayCount * 86400000);
  return `${kind}_${adjacentEnd.toISOString().slice(0, 10)}`;
}

function parseCalendarDate(value) {
  const [year, month, day] = String(value || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(Date.UTC(year, month - 1, day));
}

function formatDate(value) {
  const parsed = value instanceof Date ? value : parseCalendarDate(value);
  if (!parsed) return value || "";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(parsed);
}

export function reportingPeriodRange(period) {
  if (!period?.start_date || !period?.end_date) return period?.label || "";
  return `${formatDate(period.start_date)} – ${formatDate(period.end_date)}`;
}

export function comparisonPeriodDescription(period) {
  const start = parseCalendarDate(period?.start_date);
  const end = parseCalendarDate(period?.end_date);
  if (!start || !end) return "Score-point change versus the immediately preceding equal-length period.";
  const dayCount = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
  const previousEnd = new Date(start.getTime() - 86400000);
  const previousStart = new Date(previousEnd.getTime() - (dayCount - 1) * 86400000);
  return `Score-point change versus the immediately preceding ${dayCount} days (${formatDate(previousStart)} – ${formatDate(previousEnd)}).`;
}

export function stageDisplayLabel(value) {
  return String(value || "")
    .replace(/^(\d+)\.\s*Step\s+/i, "$1. ")
    .replace(/^Step\s+(\d+)\s*[:.-]?\s*/i, "$1. ");
}
